'use client';

import { AlertTriangle, Loader2 } from 'lucide-react';

import { BaseModal } from '@/components/ui/BaseModal';
import { Button } from '@/components/ui/button';
import { Member } from '@/lib/query/useOrganizationMembers';
import { usePricing } from '@/lib/query/usePricing';

interface RemoveMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: Member | null;
  onConfirm: () => void;
  isPending?: boolean;
}

export function RemoveMemberModal({
  isOpen,
  onClose,
  member,
  onConfirm,
  isPending = false,
}: RemoveMemberModalProps) {
  const { data: pricing } = usePricing();

  if (!member) return null;

  // Calculate the new price after removing a member
  const newTotalCents =
    pricing && pricing.totalCents !== null
      ? Math.max(0, pricing.totalCents - pricing.pricePerMemberCents)
      : null;

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: pricing?.currency || 'USD',
    }).format(cents / 100);
  };

  const displayName =
    member.firstName && member.lastName
      ? `${member.firstName} ${member.lastName}`
      : member.firstName || member.email.split('@')[0];

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Remove Member"
      size="sm"
    >
      <div className="space-y-4">
        <div className="bg-destructive/10 flex items-start gap-3 rounded-md p-4">
          <AlertTriangle className="text-destructive mt-0.5 h-5 w-5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">
              Are you sure you want to remove this member?
            </p>
            <p className="text-muted-foreground text-sm">
              <strong>{displayName}</strong> ({member.email}) will lose access
              to this organization immediately.
            </p>
          </div>
        </div>

        {pricing && newTotalCents !== null && (
          <div className="bg-muted/50 rounded-md p-3 text-sm">
            <p className="text-muted-foreground">
              Removing this member will reduce your subscription to{' '}
              <span className="text-foreground font-medium">
                {formatPrice(newTotalCents)}/month
              </span>
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Removing...
              </>
            ) : (
              'Remove Member'
            )}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
}
